import React, { useEffect, useState } from 'react';
import { adminActivateUser, adminCreateUser, adminDeactivateUser, adminGetUsers, adminUpdateUser, getSites } from '../api';
import { useAuth } from '../auth/AuthContext';

const ROLES = ['admin', 'manager', 'staff', 'user'];
const EMPTY_FORM = { Email: '', DisplayName: '', Role: 'staff', SiteId: '', IsActive: true };

export function needsSelfLockoutConfirmation(currentUser, targetUser, updates) {
  const currentId = currentUser?.appUserId || currentUser?.UserId;
  if (!currentId || !targetUser || Number(currentId) !== Number(targetUser.UserId)) return false;
  if (updates.IsActive === false) return true;
  if (targetUser.Role === 'admin' && updates.Role && updates.Role !== 'admin') return true;
  return false;
}

export default function AdminUsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  const isAdmin = String(user?.role || '').trim().toLowerCase() === 'admin';

  const load = async () => {
    setLoading(true);
    try {
      const [usersRes, sitesRes] = await Promise.all([adminGetUsers(), getSites()]);
      setUsers(usersRes.data.rows || []);
      setSites(sitesRes.data || []);
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.error || 'Failed to load users' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  if (!isAdmin) {
    return <><div className="top-header"><h1>Admin Users</h1></div><div className="page-content"><div className="hs-card"><div className="hs-card-body">Admin user management requires authenticated admin access.</div></div></div></>;
  }

  const startEdit = (target) => {
    setEditing(target);
    setForm({ Email: target.Email, DisplayName: target.DisplayName, Role: target.Role, SiteId: String(target.SiteId || ''), IsActive: !!target.IsActive });
  };

  const reset = () => { setEditing(null); setForm(EMPTY_FORM); };

  const submit = async (event) => {
    event.preventDefault();
    const payload = { ...form, SiteId: parseInt(form.SiteId, 10), IsActive: !!form.IsActive };
    if (editing) {
      if (needsSelfLockoutConfirmation(user, editing, payload) && !window.confirm('This changes your own admin access. Continue?')) return;
      if (editing.Role === 'admin' && payload.Role !== 'admin' && !window.confirm('Change this administrator to a non-admin role?')) return;
      await adminUpdateUser(editing.UserId, { DisplayName: payload.DisplayName, Role: payload.Role, SiteId: payload.SiteId, IsActive: payload.IsActive })
        .then(() => setMessage({ type: 'success', text: 'User updated' }))
        .catch(e => setMessage({ type: 'error', text: e.response?.data?.error || 'Update failed' }));
    } else {
      await adminCreateUser(payload)
        .then(() => setMessage({ type: 'success', text: 'User added' }))
        .catch(e => setMessage({ type: 'error', text: e.response?.data?.error || 'Create failed' }));
    }
    reset();
    await load();
  };

  const setActive = async (target, active) => {
    if (!active) {
      if (needsSelfLockoutConfirmation(user, target, { IsActive: false }) && !window.confirm('Deactivate your own admin account?')) return;
      if (!window.confirm(`Deactivate ${target.DisplayName}?`)) return;
    }
    const action = active ? adminActivateUser : adminDeactivateUser;
    await action(target.UserId)
      .then(() => setMessage({ type: 'success', text: active ? 'User activated' : 'User deactivated' }))
      .catch(e => setMessage({ type: 'error', text: e.response?.data?.error || 'Status update failed' }));
    await load();
  };

  return (
    <>
      <div className="top-header"><h1>Admin Users</h1><div className="top-header-actions"><span className="text-muted text-sm">Application authorization only. Users must already exist in Azure AD.</span></div></div>
      <div className="page-content">
        {message && <div className={`hs-alert hs-alert-${message.type === 'error' ? 'danger' : 'success'} mb-md`}>{message.text}</div>}

        <div className="hs-card mb-lg">
          <div className="hs-card-header"><h2>{editing ? 'Edit User' : 'Add User'}</h2></div>
          <form className="analytics-card-body analytics-filter-grid" onSubmit={submit}>
            <div><label className="text-sm text-muted">Email</label><input className="hs-input" type="email" required disabled={!!editing} value={form.Email} onChange={e => setForm({ ...form, Email: e.target.value })} /></div>
            <div><label className="text-sm text-muted">Display Name</label><input className="hs-input" required value={form.DisplayName} onChange={e => setForm({ ...form, DisplayName: e.target.value })} /></div>
            <div><label className="text-sm text-muted">Role</label><select className="hs-input" value={form.Role} onChange={e => setForm({ ...form, Role: e.target.value })}>{ROLES.map(role => <option key={role}>{role}</option>)}</select></div>
            <div><label className="text-sm text-muted">Site</label><select className="hs-input" required value={form.SiteId} onChange={e => setForm({ ...form, SiteId: e.target.value })}><option value="">Select site</option>{sites.map(site => <option key={site.SiteId} value={site.SiteId}>{site.SiteCode} - {site.SiteName} ({site.SiteId})</option>)}</select></div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}><input type="checkbox" checked={form.IsActive} onChange={e => setForm({ ...form, IsActive: e.target.checked })} /> Active</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'end' }}><button className="hs-btn hs-btn-primary" type="submit">{editing ? 'Save User' : 'Add User'}</button>{editing && <button className="hs-btn hs-btn-secondary" type="button" onClick={reset}>Cancel</button>}</div>
            <div className="text-muted text-sm" style={{ gridColumn: '1 / -1' }}>The email must match the Azure AD sign-in identity used by this application. Email is immutable after creation.</div>
          </form>
        </div>

        <div className="hs-card">
          <div className="hs-card-header"><h2>Application Users</h2></div>
          <div className="analytics-table-wrap"><table className="hs-table analytics-table"><thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Site</th><th>Status</th><th>Created</th><th>Updated</th><th>Actions</th></tr></thead><tbody>{loading ? <tr><td colSpan="8" className="text-center text-muted" style={{ padding: 24 }}>Loading users...</td></tr> : users.length === 0 ? <tr><td colSpan="8" className="text-center text-muted" style={{ padding: 24 }}>No application users found.</td></tr> : users.map(target => <tr key={target.UserId}><td>{target.DisplayName}</td><td>{target.Email}</td><td>{target.Role}</td><td>{target.Site ? `${target.Site.SiteCode || ''} ${target.Site.SiteName || ''}` : target.SiteId}</td><td>{target.IsActive ? 'Active' : 'Inactive'}</td><td>{target.CreatedAt ? new Date(target.CreatedAt).toLocaleDateString() : '-'}</td><td>{target.UpdatedAt ? new Date(target.UpdatedAt).toLocaleDateString() : '-'}</td><td><button className="hs-btn hs-btn-sm hs-btn-secondary" onClick={() => startEdit(target)}>Edit</button> {target.IsActive ? <button className="hs-btn hs-btn-sm hs-btn-secondary" onClick={() => setActive(target, false)}>Deactivate</button> : <button className="hs-btn hs-btn-sm hs-btn-primary" onClick={() => setActive(target, true)}>Activate</button>}</td></tr>)}</tbody></table></div>
        </div>
      </div>
    </>
  );
}